import React, { useState } from "react";
import axios from "axios";
import { Gift, ArrowUpRight, Check } from "lucide-react";
import { useLang } from "../LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AMOUNTS = [25000, 50000, 100000, 200000];

export default function GiftCards() {
  const { t, lang } = useLang();
  const [form, setForm] = useState({
    buyer_name: "", buyer_email: "", buyer_phone: "",
    recipient_name: "", amount_fcfa: 50000, message: "",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/gift-cards/request`, { ...form, language: lang });
      setDone(true);
    } catch {} finally { setBusy(false); }
  };

  return (
    <main data-testid="gift-cards-page" className="pt-32 min-h-screen px-6 md:px-12 lg:px-20 py-16">
      <div className="max-w-3xl mx-auto">
        <Gift size={32} strokeWidth={0.8} className="text-primary mb-6" />
        <p className="qw-overline mb-4">{t.giftCards.title}</p>
        <h1 className="font-serif text-5xl md:text-6xl text-foreground font-light tracking-tight mb-6 text-balance">
          {t.giftCards.title}
        </h1>
        <p className="text-base lg:text-lg text-muted-foreground mb-12 max-w-xl">{t.giftCards.subtitle}</p>

        {done ? (
          <div data-testid="gift-success" className="border border-primary p-10 text-center">
            <Check size={32} strokeWidth={1.2} className="text-primary mx-auto mb-6" />
            <p className="font-serif text-2xl text-foreground mb-3">{t.giftCards.title}</p>
            <p className="text-sm text-muted-foreground leading-relaxed max-w-md mx-auto">{t.giftCards.success}</p>
          </div>
        ) : (
          <form onSubmit={submit} data-testid="gift-form" className="space-y-8 max-w-2xl">
            <div>
              <label className="qw-overline block mb-3">{t.giftCards.amount}</label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {AMOUNTS.map((a) => (
                  <button
                    key={a} type="button"
                    data-testid={`gift-amount-${a}`}
                    onClick={() => setForm({ ...form, amount_fcfa: a })}
                    className={`py-4 border text-sm tracking-wider ${
                      form.amount_fcfa === a
                        ? "bg-primary text-primary-foreground border-primary"
                        : "border-border hover:border-primary hover:text-primary"
                    }`}
                  >
                    {a.toLocaleString("fr-FR")} FCFA
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <label className="qw-overline block mb-2">{t.giftCards.buyerName}</label>
                <input required data-testid="gift-buyer-name" value={form.buyer_name} onChange={(e) => setForm({...form, buyer_name: e.target.value})} className="qw-input" />
              </div>
              <div>
                <label className="qw-overline block mb-2">{t.giftCards.buyerPhone}</label>
                <input required type="tel" data-testid="gift-buyer-phone" value={form.buyer_phone} onChange={(e) => setForm({...form, buyer_phone: e.target.value})} className="qw-input" />
              </div>
            </div>
            <div>
              <label className="qw-overline block mb-2">{t.giftCards.buyerEmail}</label>
              <input required type="email" data-testid="gift-buyer-email" value={form.buyer_email} onChange={(e) => setForm({...form, buyer_email: e.target.value})} className="qw-input" />
            </div>
            <div>
              <label className="qw-overline block mb-2">{t.giftCards.recipientName}</label>
              <input required data-testid="gift-recipient-name" value={form.recipient_name} onChange={(e) => setForm({...form, recipient_name: e.target.value})} className="qw-input" />
            </div>
            <div>
              <label className="qw-overline block mb-2">{t.giftCards.message}</label>
              <textarea rows="3" data-testid="gift-message" value={form.message} onChange={(e) => setForm({...form, message: e.target.value})} className="qw-input resize-none" />
            </div>

            <button type="submit" disabled={busy} data-testid="gift-submit-btn" className="qw-btn qw-btn-primary disabled:opacity-60">
              {busy ? t.giftCards.processing : t.giftCards.submit}
              {!busy && <ArrowUpRight size={14} strokeWidth={1.2} />}
            </button>
          </form>
        )}
      </div>
    </main>
  );
}
